import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send, Clock, CheckCircle, MessageCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserTicketDetailPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user && ticketId) {
      fetchTicket();
    }
  }, [user, ticketId]);

  const fetchTicket = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicket(response.data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    const token = localStorage.getItem('token');

    try {
      await axios.post(
        `${API}/tickets/${ticketId}/messages`,
        { content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Message sent');
      setNewMessage('');
      fetchTicket(); // Refresh to show new message
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
      case 'waiting_user':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'resolved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'closed':
        return <CheckCircle className="w-5 h-5 text-gray-500" />;
      default:
        return <MessageCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      waiting_user: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      open: 'Open',
      waiting_user: 'Awaiting Your Response',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[status]}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-700',
      normal: 'bg-blue-100 text-blue-700',
      high: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[priority]}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold text-gray-700">Ticket not found</h2>
            <p className="text-gray-500 mt-2">This ticket doesn't exist or you don't have access to it.</p>
            <Button onClick={() => navigate('/support')} className="mt-4 btn-snow">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const canReply = ticket.status !== 'closed';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/support')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Support
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-sm text-gray-500">{ticket.ticket_number}</span>
                {getStatusBadge(ticket.status)}
                {getPriorityBadge(ticket.priority)}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{ticket.subject}</h1>
              <p className="text-sm text-gray-500 mt-1">
                Created on {new Date(ticket.created_at).toLocaleDateString()} at {new Date(ticket.created_at).toLocaleTimeString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(ticket.status)}
            </div>
          </div>
        </div>

        {/* Ticket Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Ticket Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500">Category</p>
                <p className="font-medium capitalize">{ticket.category}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Updated</p>
                <p className="font-medium">
                  {ticket.updated_at 
                    ? new Date(ticket.updated_at).toLocaleDateString() 
                    : 'Not updated yet'}
                </p>
              </div>
            </div>
            
            {/* Original Description */}
            <div className="border-t pt-4">
              <p className="text-sm text-gray-500 mb-2">Original Description</p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-800 whitespace-pre-wrap">{ticket.content}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Messages Thread */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Conversation ({ticket.messages?.length || 0} messages)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!ticket.messages || ticket.messages.length === 0) ? (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No messages yet</p>
                <p className="text-sm mt-1">Our support team will respond soon</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ticket.messages.map((message, index) => {
                  const isSupport = message.sender_role === 'admin' || message.sender_role === 'support';
                  return (
                    <div
                      key={message.id || index}
                      className={`flex ${isSupport ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-4 ${
                          isSupport
                            ? 'bg-blue-50 border border-blue-100'
                            : 'bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-sm font-medium ${isSupport ? 'text-blue-700' : 'text-gray-700'}`}>
                            {isSupport ? '🎧 Support Team' : '👤 You'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(message.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-800 whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reply Form */}
            {canReply && (
              <form onSubmit={handleSendMessage} className="mt-6 pt-4 border-t">
                <div className="space-y-3">
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your reply here..."
                    rows={3}
                    className="w-full"
                  />
                  <div className="flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={sending || !newMessage.trim()} 
                      className="btn-snow"
                    >
                      {sending ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Reply
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            )}

            {!canReply && (
              <div className="mt-6 pt-4 border-t text-center text-gray-500">
                <p>This ticket is closed. Please open a new ticket if you need further assistance.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Timeline */}
        {ticket.status === 'resolved' && (
          <Card>
            <CardContent className="py-6">
              <div className="flex items-center justify-center gap-3 text-green-600">
                <CheckCircle className="w-6 h-6" />
                <span className="font-medium">This ticket has been resolved</span>
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">
                If you're still experiencing issues, please create a new ticket.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UserTicketDetailPage;
